import { useMemo, useState } from "react";
import HoverTooltip from "../HoverTooltip.jsx";
import OverflowTooltip from "../OverflowTooltip.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import { GROUP_LABEL, GROUP_SHORT, REASON_LABEL, TAG_LABEL } from "../../lib/investigateFacts.js";
import { LiteSessionPanel } from "./LiteSession.jsx";
import { prefetchSessionPage } from "./TriageSessionPreview.jsx";
import { CaseSessions } from "./reviewSessions.jsx";
import { DigWord, NoteKind, stoppedWords, usd } from "./InvestigateParts.jsx";
import { WORKING_WORD } from "./InvestigateSwarm.jsx";
import { ACTION_CHIP, CAPS_CHIP, CASE_INSET, CountChip, Disclosure, FILE_LABEL, SeverityChip } from "./triageParts.jsx";

const ago = (ms, nowMs = Date.now()) => {
  if (!Number.isFinite(ms) || ms <= 0) return "";
  const m = Math.round((nowMs - ms) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h} h ago` : `${Math.round(h / 24)} days ago`;
};

// What one finding's place says beyond its group: why it sits there, and
// what else is true of it.
export function placeWords(p) {
  const reason = p.reason ? REASON_LABEL[p.group]?.[p.reason] : "";
  const stopped = p.reason === "stopped" && p.detail ? ` (${stoppedWords(p.detail)})` : "";
  const tags = (p.tags || []).map((t) => TAG_LABEL[t]).filter(Boolean);
  const source = p.source === "here" ? "checked here" : p.source === "corrected" ? "corrected by a dig" : "";
  return [reason ? `${reason}${stopped}` : "", ...tags, source].filter(Boolean).join(" \u00b7 ");
}

// The line under a case's title: Investigate's account where it wrote one,
// else why its lead finding sits where it does: the standard that met it and
// why, or what places it and the check's own words.
function caseLine(view) {
  if (view.account) return view.account;
  const lead = view.items[0];
  if (!lead) return GROUP_LABEL[view.group];
  const v = lead.verdict;
  if (lead.group === "needs_you") {
    const met = v?.metBy?.length ? `Met ${v.metBy.join(", ")}` : "Met a standard";
    return v?.standardWhy ? `${met}: ${v.standardWhy}` : met;
  }
  const title = lead.finding.title !== view.title ? lead.finding.title : "";
  const said = v ? v.correctedClaim || v.why : "";
  return [title, placeWords(lead), said].filter(Boolean).join(" \u00b7 ") || GROUP_LABEL[view.group];
}

// One of Investigate's notes: what it says, the words it rests on, and the
// moment it names, which opens the session there.
// One note a row, in one column: its kind and where it opens on the first
// line, then what it says and the words it rests on.
function NoteRow({ n, refOf, onOpenSession }) {
  const at = n.session ? refOf(n.session) : null;
  return (
    <li data-investigate-note={n.kind} className="grid min-w-0 gap-y-[3px] border-t border-[color:var(--line-soft)] py-[7px] first:border-t-0 first:pt-[2px]">
      <span className="flex min-w-0 flex-wrap items-center gap-x-[10px] gap-y-[2px]">
        <NoteKind kind={n.kind} />
        {n.source === "machine" ? <span className={NOTE_CLASS}>from the machine&rsquo;s own records</span> : null}
        {at ? (
          <button
            type="button"
            data-investigate-note-open=""
            onClick={() => onOpenSession({ session: n.session, message: n.messageId || null })}
            className={`${ACTION_CHIP} ml-auto`}
          >
            {n.messageId ? `Open ${at} here` : `Open ${at}`}
          </button>
        ) : null}
      </span>
      <span className="min-w-0 text-[11px] leading-[1.5] text-[color:var(--ink)] [overflow-wrap:anywhere]">{n.text}</span>
      {n.quote ? <span className="min-w-0 font-mono text-[10px] leading-[1.45] whitespace-pre-wrap text-[color:var(--muted)] [overflow-wrap:anywhere]">{n.quote}</span> : null}
    </li>
  );
}

// Where a dig stands on the case, and the reader's way to send one. While an
// investigator is on the case, what it is reading now.
function DigBox({ view, activity, onDig }) {
  const [question, setQuestion] = useState("");
  const dig = view.dig;
  const send = () => { onDig(view, question.trim()); setQuestion(""); };
  const status = activity?.status === "running" ? `${WORKING_WORD[activity.kind] || "Working"}: ${activity.now}`
    : activity?.status === "queued" ? "Waiting its turn behind the investigators at work\u2026" : null;
  return (
    <section data-investigate-dig-box="" className="grid gap-[5px]">
      <span className={FILE_LABEL}>Dig</span>
      {dig?.status === "done" ? (
        <span className="text-[11px] leading-[1.4] text-[color:var(--muted)]">
          Dug {ago(dig.at)}{dig.reads ? ` \u00b7 ${dig.reads} reads` : ""}{dig.costUsd ? ` \u00b7 ${usd(dig.costUsd)}` : ""}{dig.question ? ` \u00b7 asked: \u201c${dig.question}\u201d` : ""}
        </span>
      ) : dig?.status === "failed" ? (
        <span role="alert" className="text-[11px] leading-[1.4] text-[color:var(--ink)]">The last dig failed: {dig.error || "the model call did not complete"}. Dig again to retry.</span>
      ) : dig?.status === "stopped" ? (
        <span className="text-[11px] leading-[1.4] text-[color:var(--muted)]">The last dig was stopped before it finished.</span>
      ) : null}
      {status ? <span role="status" className="text-[11px] leading-[1.4] text-[color:var(--ink)]">{status}</span> : (
        <span className="flex min-w-0 items-center gap-[6px]">
          <input
            data-investigate-question=""
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
            maxLength={320}
            placeholder={dig?.status === "done" ? "Ask something the last dig left open (optional)" : "What should the dig find out? (optional)"}
            aria-label="Question for the dig"
            className="min-h-[24px] min-w-0 flex-1 rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] text-[11px] leading-[1.2] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)] focus:border-[color:var(--line-strong)]"
          />
          <button type="button" data-investigate-dig-send="" onClick={send} className={ACTION_CHIP}>{dig?.status === "done" ? "Dig again" : "Dig"}</button>
        </span>
      )}
    </section>
  );
}

// Said plainly on a case the model provider refused to check: nothing has
// verified what its findings say, so the reader judges them from the session.
function RefusedNote({ view }) {
  const refused = view.items.filter((p) => p.group === "refused").length;
  if (!refused) return null;
  return (
    <section data-investigate-refused-note="" className="grid gap-[4px]">
      <span className={FILE_LABEL}>Not verified</span>
      <span className="text-[11px] leading-[1.45] text-[color:var(--muted)]">
        The model provider refused to check {refused === 1 ? (view.items.length === 1 ? "this finding" : "one of these findings") : `${refused} of these findings`}, so nothing has verified what {refused === 1 ? "it says" : "they say"}. Read the session to judge {refused === 1 ? "it" : "them"} yourself.
      </span>
    </section>
  );
}

// Each session's findings in a case, as the Triage tab's session panel reads
// them, with the verdict each is placed by.
export function caseJobs(view) {
  return view.sessions.map((session) => {
    const items = view.items.filter((p) => String(p.finding.session).toLowerCase() === session);
    return {
      id: `${view.id}:${session}`, session, conducts: 1,
      findings: items.map((p) => p.finding),
      verdicts: Object.fromEntries(items.filter((p) => p.verdict).map((p) => [p.finding.id, p.verdict])),
    };
  });
}
// A case's sessions read ahead, as the pointer comes to its row.
export const prefetchCase = (jobs) => { for (const j of jobs.slice(0, 2)) prefetchSessionPage(j.session, j.findings.map((f) => ({ messageIds: f.messages || [] }))); };

// Everything Investigate knows of a case, opened under its row: the case and
// what digs found, side by side where there is room, and each session's
// evidence as the Triage tab's session panel reads it. The tab's cases open
// into it, and so do the welcome page's.
export function CaseBody({ view, activity = null, labels, conductName, onDig, onWorkspace, onOpenSession, onChat = null }) {
  const sessionRefs = useMemo(() => new Map(view.sessions.map((s, i) => [s, `S${i + 1}`])), [view.sessions]);
  const refOf = (session) => sessionRefs.get(String(session).toLowerCase()) || "the session";
  const jobs = useMemo(() => caseJobs(view), [view]);
  return (
    <div data-investigate-case-body="" className="grid cursor-auto gap-[14px] pt-[2px] pb-[12px]" style={{ paddingLeft: CASE_INSET, paddingRight: 6 }}>
      {/* Side by side when the tab is wide enough, by the tab's own width:
          the lens shares the page with the chat column. */}
      <div className="@container/case min-w-0">
      <div className="grid min-w-0 gap-x-[32px] gap-y-[14px] @min-[760px]/case:grid-cols-2">
        <div className="grid min-w-0 content-start gap-[12px]">
          <section className="grid gap-[4px]">
            <span className={FILE_LABEL}>{view.provisional ? "Not organized yet" : "The case"}</span>
            {view.account ? <p className="m-0 text-[12px] leading-[1.5] text-[color:var(--ink)] [overflow-wrap:anywhere]">{view.account}</p> : (
              <span className="text-[11px] leading-[1.45] text-[color:var(--muted)]">
                {view.provisional ? "These findings quote the same evidence in one session. Organizing reads them beside the rest and folds them into a story." : "Investigate hasn\u2019t written an account of this case yet."}
              </span>
            )}
            {view.dig?.status === "done" && view.dig.why ? (
              <span className="text-[11px] leading-[1.45] text-[color:var(--ink)]"><DigWord dig={view.dig} />{" "}{view.dig.why}</span>
            ) : null}
          </section>
          {/* What joined its sessions, where a case was formed by what links
              them (the welcome page's cases): which two, and why. */}
          {view.sessions.length > 1 && view.links?.length ? (
            <section data-case-links="" className="grid gap-[4px]">
              <span className={FILE_LABEL}>Why these sessions are one case</span>
              <ul className="m-0 grid list-none gap-[2px] p-0">
                {view.links.map((l) => (
                  <li key={`${l.a}:${l.b}`} className="text-[11px] leading-[1.45] text-[color:var(--muted)]">
                    <span className="text-[color:var(--ink)]">{[refOf(l.a), refOf(l.b)].sort((x, y) => x.localeCompare(y, undefined, { numeric: true })).join(" and ")}</span>{`: ${l.why}`}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          <RefusedNote view={view} />
          <DigBox view={view} activity={activity} onDig={onDig} />
          <div data-investigate-case-actions="" className="flex flex-wrap items-center gap-[6px]">
            <HoverTooltip label="Opens the case on the trace workspace: its sessions side by side, its findings pinned open, and these notes at their moments. Closing it brings you back here." placement="top" wide>
              <button type="button" data-investigate-workspace-open="" onClick={() => onWorkspace(view)} className={`${ACTION_CHIP} border-[color:var(--line-strong)] text-[color:var(--ink)]`}>Open on the workspace</button>
            </HoverTooltip>
            {onChat ? (
              <HoverTooltip label="Puts this case in the chat, with its findings, the checks and what digs found: the chat reads its sessions, and what it notes is kept with the case" placement="top" wide>
                <button type="button" data-investigate-chat="" onClick={() => onChat(view)} className={ACTION_CHIP}>Add to chat</button>
              </HoverTooltip>
            ) : null}
          </div>
        </div>
        <div className="grid min-w-0 content-start gap-[12px]">
          <section className="grid gap-[4px]">
            <span className={FILE_LABEL}>What Investigate found</span>
            {view.notes.length ? (
              <ul data-investigate-notes="" className="m-0 grid list-none p-0">
                {view.notes.map((n) => <NoteRow key={n.id} n={n} refOf={refOf} onOpenSession={onOpenSession} />)}
              </ul>
            ) : (
              <span className="text-[11px] leading-[1.45] text-[color:var(--muted)]">{view.dig?.status === "done" ? "The dig left no notes." : "Nothing yet. A dig reads the sessions and what ran on the machine, and notes what matters and what the first passes missed."}</span>
            )}
          </section>
          {view.related.length ? (
            <section data-investigate-related="" className="grid gap-[4px]">
              <span className={FILE_LABEL}>Related sessions</span>
              <ul className="m-0 grid list-none gap-[4px] p-0">
                {view.related.map((r) => (
                  <li key={r.session} className="flex min-w-0 items-baseline gap-x-[8px]">
                    <button type="button" onClick={() => onOpenSession({ session: r.session })} className={ACTION_CHIP}>{labels?.get(r.session) ? "Open" : r.session.slice(0, 8)}</button>
                    <span className="min-w-0 text-[11px] leading-[1.45] text-[color:var(--ink)]">{labels?.get(r.session) ? <span className="text-[color:var(--muted)]">{labels.get(r.session)}: </span> : null}{r.why}</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      </div>
      </div>
      {jobs.map((job) => (
        <div key={job.id} data-investigate-session={job.session} className="grid border-t border-[color:var(--line-soft)] pt-[6px]">
          {jobs.length > 1 ? <span className={`${FILE_LABEL} pb-[2px]`}>{refOf(job.session)}</span> : null}
          <LiteSessionPanel
            job={job}
            verdicts={job.verdicts}
            conductName={conductName}
            summary={labels?.get(job.session) || null}
            onOpen={(member) => onOpenSession({ session: job.session, message: member?.messages?.[0] || null })}
          />
        </div>
      ))}
    </div>
  );
}

// A case, a row of the tab: its story in a line, and opened, everything
// Investigate knows of it and each session's evidence.
export default function InvestigateCase({
  view, hero = false, open, onToggle, labels, machineFor, conductName, activity = null,
  onDig, onWorkspace, onOpenSession, onChat = null,
}) {
  const busy = activity?.status === "running";
  const queued = activity?.status === "queued";
  const others = view.groups.filter((g) => g !== view.group);
  const missed = view.notes.filter((n) => n.kind === "missed" || n.kind === "corrects").length;
  const caseForSessions = useMemo(() => ({ sessions: view.sessions, endpoints: view.endpoints, sessionLabel: null }), [view.sessions, view.endpoints]);
  const jobs = useMemo(() => caseJobs(view), [view]);
  const hover = () => prefetchCase(jobs);
  return (
    <div
      data-investigate-case={view.id}
      data-investigate-group={view.group}
      data-investigate-provisional={view.provisional ? "" : undefined}
      className={`grid min-w-0 border-t border-[color:var(--line-soft)] transition-colors ${open ? "bg-[color:var(--panel)]" : ""}`}
    >
      <div
        onClick={onToggle}
        onMouseEnter={hover}
        className={`@container group/case relative flex min-w-0 cursor-pointer items-center gap-x-[12px] px-[6px] py-[8px] ${open ? "" : "hover:bg-[color:var(--control-bg)]"}`}
      >
        <Disclosure data-investigate-case-toggle="" open={open} label={open ? "Close the case" : "Open the case"} onToggle={onToggle} />
        <SeverityChip severity={view.severity} />
        <span className="grid min-w-0 flex-1 gap-[2px]">
          <span className="flex min-w-0 items-center gap-x-[8px]">
            <OverflowTooltip text={view.title} className={`min-w-0 truncate text-[12px] leading-[1.2] text-[color:var(--ink)] ${hero ? "font-medium" : ""}`} />
            {missed ? (
              <HoverTooltip label={`${missed} note${missed === 1 ? "" : "s"} on what the review and its check missed or got wrong`} placement="top">
                <span data-investigate-missed={missed} className={`${CAPS_CHIP} border-current`} style={{ color: "var(--danger, #d4503e)" }}>Missed {missed}</span>
              </HoverTooltip>
            ) : null}
          </span>
          {busy ? (
            <span data-investigate-case-now="" className="min-w-0 truncate text-[11px] leading-[1.25] text-[color:var(--muted)]">{activity.now}</span>
          ) : <OverflowTooltip text={caseLine(view)} className="min-w-0 truncate text-[11px] leading-[1.25] text-[color:var(--muted)]" />}
        </span>
        <span className="flex shrink-0 items-center gap-x-[10px]">
          {view.matches.length ? (
            <HoverTooltip label={`Bears on your priorit${view.matches.length === 1 ? "y" : "ies"}: ${view.matches.join("; ")}`} placement="top" wide>
              <span data-investigate-matches="" className={`${CAPS_CHIP} border-[color:var(--line-strong)] text-[color:var(--ink)]`}>Priority</span>
            </HoverTooltip>
          ) : null}
          {others.length ? (
            <HoverTooltip label={`This case also holds findings that are: ${others.map((g) => GROUP_LABEL[g].toLowerCase()).join(", ")}. They are counted here, under the case's most pressing group.`} placement="top" wide>
              <span data-investigate-also="" className="hidden max-w-[150px] truncate text-[10px] leading-[15px] text-[color:var(--muted)] @min-[760px]:inline">
                also {GROUP_SHORT[others[0]]}{others.length > 1 ? ` +${others.length - 1}` : ""}
              </span>
            </HoverTooltip>
          ) : null}
          {busy ? <span role="status" data-investigate-working={activity.kind} className="text-[9px] leading-[15px] tracking-[0.08em] uppercase text-[color:var(--ink)] motion-safe:animate-pulse">{WORKING_WORD[activity.kind] || "Working"}</span> : queued ? <span data-investigate-queued="" className="text-[9px] leading-[15px] tracking-[0.08em] uppercase text-[color:var(--muted)]">Queued</span> : <DigWord dig={view.dig} />}
          <span className="hidden @min-[640px]:flex">
            <CaseSessions c={caseForSessions} labels={labels} machineFor={machineFor} />
          </span>
          <CountChip count={view.items.length} />
          <button
            type="button"
            data-investigate-workspace=""
            onClick={(e) => { e.stopPropagation(); onWorkspace(view); }}
            className={`${ACTION_CHIP} ${open ? "" : "opacity-0 group-hover/case:opacity-100 focus-visible:opacity-100"}`}
          >
            Workspace
          </button>
        </span>
      </div>
      {open ? (
        <CaseBody
          view={view} activity={activity} labels={labels} conductName={conductName}
          onDig={onDig} onWorkspace={onWorkspace} onOpenSession={onOpenSession} onChat={onChat}
        />
      ) : null}
    </div>
  );
}
